import React from 'react';
import { Truck, Edit2, FileUp, Clock, X, Send, Check } from 'lucide-react';
import { CustomerDeadlineView, StatusField, Customer, SnoozedDeadlineInfo, DatenAnlieferungInfo, LohnscheinNachzuegler, SpezialfristNachzuegler, IndividualNachzuegler } from '../../../shared/types';
import { formatDate, getLohnscheinPeriode } from '../../../shared/utils/dateUtils';
import { calculateIsOverdue, getIndividualDeadlineLabel, getIndividualDeadlineKey, getIndividualNachzueglerKey, isDeadlineSnoozed, getSnoozedInfo, hasOppmQrVersand, hasMonatsuebersichtQrVersand, ermittleRuhendeFristen } from '../../../shared/utils/deadlineUtils';
import { buildZendeskAgentSuchUrl } from '../../../shared/services/zendesk';

interface CustomerDeadlineCardProps {
  view: CustomerDeadlineView;
  onToggleStatus: (view: CustomerDeadlineView, field: StatusField) => void;
  onResetMonth?: (view: CustomerDeadlineView) => void;
  onEditCustomer?: (customer: Customer) => void;
  onSendDocuments?: (customer: Customer) => void;
  isButtonLoading?: (customerId: string, field: StatusField) => boolean;
  onSnoozeDeadline?: (view: CustomerDeadlineView, field: StatusField) => void;
  // FRIST AB ANLIEFERUNG: Datenanlieferung markieren / zurücknehmen
  onMarkDatenAnlieferung?: (view: CustomerDeadlineView, field: StatusField) => void;
  onUnmarkDatenAnlieferung?: (view: CustomerDeadlineView, field: StatusField) => void;
  // WARTET AUF KUNDEN: "An Kunden geliefert" und Kundenrückmeldung markieren /
  // zurücknehmen. Nur für Lohnlauf-Fristen mit markierter Datenanlieferung.
  onMarkWartetAufKunden?: (view: CustomerDeadlineView, field: StatusField) => void;
  onUnmarkWartetAufKunden?: (view: CustomerDeadlineView, field: StatusField) => void;
  onMarkKundenRueckmeldung?: (view: CustomerDeadlineView, field: StatusField) => void;
  onUnmarkKundenRueckmeldung?: (view: CustomerDeadlineView, field: StatusField) => void;
  // NACHZÜGLER: eigener Klickpfad für offene Lohnschein-Läufe aus dem zuletzt
  // abgeschlossenen Monat. Bewusst getrennt von onToggleStatus, weil diese
  // Kachel nicht zum laufenden Arbeitsmonat gehört.
  onOpenLohnscheinNachzuegler?: (view: CustomerDeadlineView, nachzuegler: LohnscheinNachzuegler) => void;
  // NACHZÜGLER SPEZIALFRISTEN: eigener Klickpfad für offene QR-Fristen aus dem
  // zuletzt abgeschlossenen Monat. Gleiche Begründung wie oben: diese Kachel
  // gehört nicht zum laufenden Arbeitsmonat.
  onOpenSpezialfristNachzuegler?: (view: CustomerDeadlineView, nachzuegler: SpezialfristNachzuegler) => void;
  // NACHZÜGLER INDIVIDUELLE FRISTEN: eigener Klickpfad für offene individuelle
  // Fristen aus dem zuletzt abgeschlossenen Monat. Der Klick erledigt die Frist
  // für diesen Monat. Gleiche Begründung wie oben.
  onCompleteIndividualNachzuegler?: (view: CustomerDeadlineView, nachzuegler: IndividualNachzuegler) => void;
}

interface DeadlineItem {
  key: string;
  fieldName: StatusField;
  title: string;
  date: Date | undefined;
  completed: boolean;
  type: 'standard' | 'individual';
  description?: string; // NEUE PROPERTY für Beschreibungen
  isOneTime?: boolean; // NEUE PROPERTY für einmalige Deadlines
  isSnoozed?: boolean; // SNOOZE FEATURE: Ist die Deadline zurückgestellt?
  snoozeInfo?: SnoozedDeadlineInfo | null; // SNOOZE FEATURE: Details zur Zurückstellung
  fristAbAnlieferung?: boolean; // FRIST AB ANLIEFERUNG: Toggle am Kunden aktiv?
  anlieferungInfo?: DatenAnlieferungInfo | null; // FRIST AB ANLIEFERUNG: Details zur Markierung
  // Punkt 4 (Lohnschein-Versand asynchron): offener Auftrag -> gelber Wartezustand
  auftragWartend?: boolean;
  auftragFeedback?: string | null;
  // Auftrag eskaliert -> roter Zustand. Es wurde NICHTS versendet, der Auftrag
  // wurde bewusst angehalten. Hat Vorrang vor dem gelben Wartezustand.
  auftragEskaliert?: boolean;
  // NACHZÜGLER: gesetzt, wenn diese Kachel zu einem bereits abgeschlossenen
  // Monat gehört. Steuert Klickpfad, Rand, Badge und das Fehlen des Snooze-Knopfs.
  nachzuegler?: LohnscheinNachzuegler;
  // NACHZÜGLER SPEZIALFRISTEN: dito für die beiden QR-Fristen. Bewusst ein
  // eigenes Feld, weil der Klickpfad ein anderer ist.
  spezialfristNachzuegler?: SpezialfristNachzuegler;
  // NACHZÜGLER INDIVIDUELLE FRISTEN: dito für individuelle Fristen mit
  // offen_nach_finalisierung_ab. Eigenes Feld, weil der Klickpfad ein anderer ist.
  individualNachzuegler?: IndividualNachzuegler;
}

export const CustomerDeadlineCard: React.FC<CustomerDeadlineCardProps> = ({ 
  view, 
  onToggleStatus, 
  onResetMonth,
  onEditCustomer,
  onSendDocuments,
  isButtonLoading = () => false,
  onSnoozeDeadline,
  onMarkDatenAnlieferung,
  onUnmarkDatenAnlieferung,
  onMarkWartetAufKunden,
  onUnmarkWartetAufKunden,
  onMarkKundenRueckmeldung,
  onUnmarkKundenRueckmeldung,
  onOpenLohnscheinNachzuegler,
  onOpenSpezialfristNachzuegler,
  onCompleteIndividualNachzuegler
}) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [showResetConfirm, setShowResetConfirm] = React.useState(false);
  const [hoveredDeadlineId, setHoveredDeadlineId] = React.useState<string | null>(null);
  const [hoveredSnoozeId, setHoveredSnoozeId] = React.useState<string | null>(null);
  
  const isCurrentlyOverdue = calculateIsOverdue(
    view.deadlines,
    view.status,
    view.customer,
    view.individualDeadlines,
    view.nachzuegler,
    { lauf1: view.lohnscheinCompleted1, lauf2: view.lohnscheinCompleted2 },
    { oppmQr: view.oppmQrCompleted, monatsuebersichtQr: view.monatsuebersichtQrCompleted },
    view.spezialfristNachzuegler,
    view.lohnscheinRelativ,
    view.individualNachzuegler
  );

  // WARTET AUF KUNDEN: Fristen, die gerade beim Kunden ruhen (der Lohnlauf und
  // die daran hängenden relativen Fristen). Eine Quelle für Titel, Farbe,
  // Klickverhalten und Datumszeile der Kacheln.
  const ruhendeFristen = ermittleRuhendeFristen(view.customer, view.status, view.individualDeadlines, view.lohnscheinRelativ);

  const getDisplayName = (customer: Customer): string => {
    const vorname = customer.vorname?.trim();
    const nachname = customer.nachname?.trim();
    
    if (!vorname || !nachname) {
      return 'Unbekannt';
    }
    
    return `${vorname} ${nachname}`;
  };

  const getStatusIcon = (completed: boolean, isLoading: boolean = false, isOneTime: boolean = false): string => {
    if (isLoading) return '';
    if (isOneTime && completed) return '✔️'; // Spezielle Markierung für erledigte einmalige Deadlines
    return completed ? '✅' : '⏱️';
  };

  // FRIST AB ANLIEFERUNG: Prüft, ob eine Anlieferungsfrist aktuell "aktiv läuft"
  // (Anlieferung markiert, noch nicht erledigt, noch nicht überfällig).
  // Überfälligkeit behält Vorrang (Rot), Snooze-Gelb ebenfalls — Orange greift
  // nur im Fenster zwischen Markierung und Fälligkeit. Ruhende Fristen
  // (Wartet auf Kunden) werden am Aufrufort ausgeschlossen.
  const isAnlieferungActive = (deadline: DeadlineItem): boolean => {
    return !!(
      deadline.fristAbAnlieferung &&
      deadline.anlieferungInfo &&
      !deadline.completed &&
      deadline.date &&
      deadline.date >= new Date()
    );
  };

  // FRIST AB ANLIEFERUNG: Prüft, ob eine Lohnlauf-Frist im Wartezustand ist
  // (Toggle aktiv, aber noch keine Anlieferung markiert und nicht erledigt).
  // Im Wartezustand markiert der reguläre Kachel-Klick die Datenanlieferung
  // statt die Erledigung zu togglen.
  const isWaitingForDaten = (deadline: DeadlineItem): boolean => {
    return !!(
      deadline.fristAbAnlieferung &&
      !deadline.anlieferungInfo &&
      !deadline.completed
    );
  };

  const getOverdueStyle = (deadline: Date | undefined, completed: boolean, isLoading: boolean = false, isSnoozed: boolean = false, anlieferungActive: boolean = false, auftragWartend: boolean = false, auftragEskaliert: boolean = false, ruhend: boolean = false): React.CSSProperties => {
    if (completed) return {};
    
    if (isLoading) {
      return { 
        backgroundColor: '#F3F4F6', 
        color: '#6B7280',
        cursor: 'not-allowed',
        opacity: 0.7
      };
    }
    
    // Auftrag eskaliert: es wurde NICHTS versendet und niemand hat entschieden.
    // Höchste Sichtbarkeit, Vorrang vor Wartezustand und Überfälligkeit.
    if (auftragEskaliert) {
      return { backgroundColor: '#FEE2E2', color: '#991B1B', border: '2px solid #DC2626' };
    }

    // Punkt 4 (Lohnschein-Versand asynchron): Auftrag beauftragt, aber noch
    // nicht versendet -> Gelb ("beauftragt, wartend"). Vorrang vor Überfälligkeit.
    if (auftragWartend) {
      return { backgroundColor: '#FEF3C7', color: '#92400E' };
    }

    // WARTET AUF KUNDEN: Frist ruht bis zur Kundenrückmeldung -> dauerhaft
    // Gelb, nie Rot. Kein Handlungsbedarf auf unserer Seite.
    if (ruhend) {
      return { backgroundColor: '#FEF3C7', color: '#92400E' };
    }

    if (!deadline) return {};

    const now = new Date();
    if (deadline < now) {
      // SNOOZE FEATURE: Gesnoozde Deadlines bekommen Gelb statt Rot
      if (isSnoozed) {
        return { backgroundColor: '#FEF3C7', color: '#92400E' };
      }
      return { backgroundColor: '#FEE2E2', color: '#991B1B' };
    }
    
    // FRIST AB ANLIEFERUNG: Aktiv laufende Anlieferungsfrist → Orange
    // ("wir sind am Zug"), Umrandung + Puls kommen über die CSS-Klasse
    // anlieferung-active am Kachel-Container.
    if (anlieferungActive) {
      return { backgroundColor: '#FFEDD5', color: '#9A3412' };
    }
    
    return {};
  };

  const getTicketsBadgeStyle = (count: number | undefined): React.CSSProperties => {
    if (count === undefined) return { display: 'none' };
    
    const baseStyle = {
      padding: '3px 6px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: 'bold' as const,
      marginLeft: '8px',
      cursor: 'pointer',
      textDecoration: 'none',
      transition: 'opacity 0.2s'
    };

    if (count === 0) {
      return {
        ...baseStyle,
        backgroundColor: '#D1FAE5',
        color: '#065F46'
      };
    } else if (count <= 5) {
      return {
        ...baseStyle,
        backgroundColor: '#FEF3C7',
        color: '#92400E'
      };
    } else {
      return {
        ...baseStyle,
        backgroundColor: '#FEE2E2',
        color: '#991B1B'
      };
    }
  };

  const getVersandmethodeText = (versandmethode?: string): string => {
    switch (versandmethode || 'email') {
      case 'email':
        return 'E-Mail';
      case 'duo':
        return 'DATEV Unternehmen Online';
      case 'kein_versand':
        return 'Kein Versand';
      case 'xtax_portal':
        return 'XTAX-Portal';
      default:
        return 'E-Mail';
    }
  };

  const getAbrechnungsmethodeText = (abrechnungsmethode?: string): string => {
    switch (abrechnungsmethode || 'lug') {
      case 'lug':
        return 'Lohn und Gehalt';
      case 'lodas':
        return 'LODAS';
      default:
        return 'Lohn und Gehalt';
    }
  };

  // FRIST AB ANLIEFERUNG: Im Wartezustand (Toggle aktiv, keine Anlieferung
  // markiert) zeigt die Kachel "wartet auf Abrechnungsdaten" statt des
  // regulären Namens. Nach der Markierung springt der Titel zurück.
  // WARTET AUF KUNDEN: Liegt die Abrechnung beim Kunden, zeigt die Kachel
  // "wartet auf Kunden". Nach der Rückmeldung springt der Titel zurück.
  const getLohnlaufLabel = (lohnlaufType: 'first' | 'second', waitingForDaten: boolean = false, wartetAufKunden: boolean = false): string => {
    const hasSecondLohnlauf = !!view.customer.zweiter_lohnlauf_tag;
    
    if (lohnlaufType === 'first') {
      if (waitingForDaten) {
        return hasSecondLohnlauf ? 'Erster Lohnlauf: wartet auf Abrechnungsdaten' : 'Abrechnung: wartet auf Abrechnungsdaten';
      }
      if (wartetAufKunden) {
        return hasSecondLohnlauf ? 'Erster Lohnlauf: wartet auf Kunden' : 'Abrechnung: wartet auf Kunden';
      }
      return hasSecondLohnlauf ? 'Erster Lohnlauf' : 'Abrechnung finalisieren';
    } else {
      if (waitingForDaten) {
        return 'Zweiter Lohnlauf: wartet auf Abrechnungsdaten';
      }
      if (wartetAufKunden) {
        return 'Zweiter Lohnlauf: wartet auf Kunden';
      }
      return 'Zweiter Lohnlauf und Abrechnung finalisieren';
    }
  };

  // Kurzform MM/JJJJ für Kachel-Titel und Nachzügler-Badge
  const monatLabel = (year: number, month: number): string =>
    `${String(month).padStart(2, '0')}/${year}`;

  // FRIST AB ANLIEFERUNG / WARTET AUF KUNDEN: Datum und Uhrzeit einer
  // Markierung für die Textzeilen unten in der Kachel. Der Tag kommt aus dem
  // gespeicherten Datum, die Uhrzeit aus dem Zeitstempel des Klicks.
  const formatZeitpunkt = (datum: string, zeitpunkt?: string): string => {
    const tag = formatDate(new Date(datum + 'T00:00:00'));
    if (!zeitpunkt) return tag;
    const z = new Date(zeitpunkt);
    if (isNaN(z.getTime())) return tag;
    return `${tag}, ${String(z.getHours()).padStart(2, '0')}:${String(z.getMinutes()).padStart(2, '0')}`;
  };

  // ZENDESK: Stand der Ticketzahl für den Tooltip am Badge. Bei einem Stand von
  // heute nur die Uhrzeit, sonst mit Datum. Leer, wenn kein Stand vorliegt.
  const formatTicketStand = (stand?: string | null): string => {
    if (!stand) return '';
    const z = new Date(stand);
    if (isNaN(z.getTime())) return '';
    const uhrzeit = `${String(z.getHours()).padStart(2, '0')}:${String(z.getMinutes()).padStart(2, '0')} Uhr`;
    const istHeute = z.toDateString() === new Date().toDateString();
    return `Stand: ${istHeute ? uhrzeit : `${formatDate(z)}, ${uhrzeit}`}`;
  };

  // Auftraggeber-Name ermitteln: JOIN-Feld bevorzugt, Fallback auf altes Freitext-Feld
  const getAuftraggeberName = (): string | null => {
    if (view.customer.auftraggeber?.company) {
      return view.customer.auftraggeber.company;
    }
    // Fallback auf altes Freitext-Feld für Bestandsdaten
    if (view.customer.im_auftrag_von) {
      return view.customer.im_auftrag_von;
    }
    return null;
  };

  const getSortedDeadlines = (): DeadlineItem[] => {
  const deadlines: DeadlineItem[] = [];

  if (view.deadlines.lohnlauf_1) {
    const lohnlauf1Waiting = !!(
      (view.customer.erster_lohnlauf_frist_ab_anlieferung || false) &&
      !view.status?.daten_anlieferungen?.['lohnlauf_1_completed'] &&
      !view.status?.lohnlauf_1_completed
    );
    deadlines.push({
      key: 'lohnlauf_1',
      fieldName: 'lohnlauf_1_completed',
      title: getLohnlaufLabel('first', lohnlauf1Waiting, ruhendeFristen.has('lohnlauf_1_completed')),
      date: view.deadlines.lohnlauf_1,
      completed: view.status?.lohnlauf_1_completed || false,
      type: 'standard',
      isSnoozed: isDeadlineSnoozed('lohnlauf_1_completed', view.status),
      snoozeInfo: getSnoozedInfo('lohnlauf_1_completed', view.status),
      fristAbAnlieferung: view.customer.erster_lohnlauf_frist_ab_anlieferung || false,
      anlieferungInfo: view.status?.daten_anlieferungen?.['lohnlauf_1_completed'] || null
    });
  }

  if (view.customer.zweiter_lohnlauf_tag && view.deadlines.lohnlauf_2) {
    const lohnlauf2Waiting = !!(
      (view.customer.zweiter_lohnlauf_frist_ab_anlieferung || false) &&
      !view.status?.daten_anlieferungen?.['lohnlauf_2_completed'] &&
      !view.status?.lohnlauf_2_completed
    );
    deadlines.push({
      key: 'lohnlauf_2',
      fieldName: 'lohnlauf_2_completed',
      title: getLohnlaufLabel('second', lohnlauf2Waiting, ruhendeFristen.has('lohnlauf_2_completed')),
      date: view.deadlines.lohnlauf_2,
      completed: view.status?.lohnlauf_2_completed || false,
      type: 'standard',
      isSnoozed: isDeadlineSnoozed('lohnlauf_2_completed', view.status),
      snoozeInfo: getSnoozedInfo('lohnlauf_2_completed', view.status),
      fristAbAnlieferung: view.customer.zweiter_lohnlauf_frist_ab_anlieferung || false,
      anlieferungInfo: view.status?.daten_anlieferungen?.['lohnlauf_2_completed'] || null
    });
  }

  if (view.deadlines.lohnsteuer) {
    deadlines.push({
      key: 'lohnsteuer',
      fieldName: 'lohnsteuer_completed',
      title: 'Lohnsteuer',
      date: view.deadlines.lohnsteuer,
      completed: view.status?.lohnsteuer_completed || false,
      type: 'standard',
      isSnoozed: isDeadlineSnoozed('lohnsteuer_completed', view.status),
      snoozeInfo: getSnoozedInfo('lohnsteuer_completed', view.status)
    });
  }

  if (view.customer.sv_pflicht && view.deadlines.sv_beitraege) {
    deadlines.push({
      key: 'sv_beitraege',
      fieldName: 'sv_beitraege_completed',
      title: 'SV-Beiträge',
      date: view.deadlines.sv_beitraege,
      completed: view.status?.sv_beitraege_completed || false,
      type: 'standard',
      isSnoozed: isDeadlineSnoozed('sv_beitraege_completed', view.status),
      snoozeInfo: getSnoozedInfo('sv_beitraege_completed', view.status)
    });
  }

  // Lohnschein-Versand an Arbeitnehmer (Zwei-Lauf-Modell): eine Kachel je Lauf.
  // Fälligkeit und Inhaltsmonat kommen pro Lauf aus dem Hook, die Erledigung
  // aus den View-Feldern lohnscheinCompleted1/2 (Quelle: lohnschein_versand_lauf_status).
  if (view.deadlines.lohnschein_versand_1) {
    const periode1 = view.lohnscheinVersandPeriode1 ?? getLohnscheinPeriode(view.currentYear, view.currentMonth);
    const auftrag1Offen = !!(view.lohnscheinVersandAuftrag1 && !view.lohnscheinCompleted1);
    const auftrag1Eskaliert = auftrag1Offen && view.lohnscheinVersandAuftrag1?.status === 'eskaliert';
    deadlines.push({
      key: 'lohnschein_versand_1',
      fieldName: 'lohnschein_versand_1_completed',
      title: `Lohnschein-Versand 1. Lohnlauf (für ${monatLabel(periode1.year, periode1.month)})`,
      date: view.deadlines.lohnschein_versand_1,
      completed: view.lohnscheinCompleted1 || false,
      type: 'standard',
      isSnoozed: isDeadlineSnoozed('lohnschein_versand_1_completed', view.status),
      snoozeInfo: getSnoozedInfo('lohnschein_versand_1_completed', view.status),
      auftragWartend: auftrag1Offen && !auftrag1Eskaliert,
      auftragEskaliert: auftrag1Eskaliert,
      auftragFeedback: view.lohnscheinVersandAuftrag1?.last_error ?? null
    });
  }

  if (view.deadlines.lohnschein_versand_2) {
    const periode2 = view.lohnscheinVersandPeriode2 ?? getLohnscheinPeriode(view.currentYear, view.currentMonth);
    const auftrag2Offen = !!(view.lohnscheinVersandAuftrag2 && !view.lohnscheinCompleted2);
    const auftrag2Eskaliert = auftrag2Offen && view.lohnscheinVersandAuftrag2?.status === 'eskaliert';
    deadlines.push({
      key: 'lohnschein_versand_2',
      fieldName: 'lohnschein_versand_2_completed',
      title: `Lohnschein-Versand 2. Lohnlauf (für ${monatLabel(periode2.year, periode2.month)})`,
      date: view.deadlines.lohnschein_versand_2,
      completed: view.lohnscheinCompleted2 || false,
      type: 'standard',
      isSnoozed: isDeadlineSnoozed('lohnschein_versand_2_completed', view.status),
      snoozeInfo: getSnoozedInfo('lohnschein_versand_2_completed', view.status),
      auftragWartend: auftrag2Offen && !auftrag2Eskaliert,
      auftragEskaliert: auftrag2Eskaliert,
      auftragFeedback: view.lohnscheinVersandAuftrag2?.last_error ?? null
    });
  }

  // NACHZÜGLER: offene Lohnschein-Läufe aus dem zuletzt abgeschlossenen Monat.
  // Ohne diese Kacheln verschwindet eine bei der Finalisierung noch offene
  // Lohnschein-Frist mit dem Monatswechsel aus der Oberfläche.
  (view.nachzuegler || []).forEach(n => {
    const eskaliert = n.auftrag?.status === 'eskaliert';
    deadlines.push({
      key: `nachzuegler_${n.arbeitsjahr}_${n.arbeitsmonat}_${n.lohnlauf}`,
      fieldName: n.lohnlauf === 1 ? 'lohnschein_versand_1_completed' : 'lohnschein_versand_2_completed',
      title: `Lohnschein-Versand ${n.lohnlauf}. Lohnlauf (für ${monatLabel(n.periode.year, n.periode.month)})`,
      date: n.faelligkeit,
      completed: false,
      type: 'standard',
      nachzuegler: n,
      auftragWartend: !!n.auftrag && !eskaliert,
      auftragEskaliert: eskaliert,
      auftragFeedback: n.auftrag?.last_error ?? null
    });
  });

  // OPPM QR-Code-Versand: Abrechnungsperiode und Erledigung kommen aus der View
  // (Quelle: customer_periodenfrist_status). Bei relativem Lohnlauf-Bezug ist
  // die Periode der Arbeitsmonat, bei festem Tag der Vormonat; abgeleitet wird
  // sie in dateUtils, nicht hier. Gelber Wartezustand bei offenem Pending-Task.
  if (hasOppmQrVersand(view.customer) && view.deadlines.oppm_qr_versand) {
    const oppmPeriode = view.oppmQrPeriode ?? getLohnscheinPeriode(view.currentYear, view.currentMonth);
    const oppmErledigt = view.oppmQrCompleted || false;
    deadlines.push({
      key: 'oppm_qr_versand',
      fieldName: 'oppm_qr_versand_completed',
      title: `OPPM QR-Code-Versand (für ${monatLabel(oppmPeriode.year, oppmPeriode.month)})`,
      date: view.deadlines.oppm_qr_versand,
      completed: oppmErledigt,
      type: 'standard',
      isSnoozed: isDeadlineSnoozed('oppm_qr_versand_completed', view.status),
      snoozeInfo: getSnoozedInfo('oppm_qr_versand_completed', view.status),
      auftragWartend: !!(view.oppmQrVersandTask && !oppmErledigt) && view.oppmQrVersandTask?.status !== 'eskaliert',
      auftragEskaliert: view.oppmQrVersandTask?.status === 'eskaliert',
      auftragFeedback: view.oppmQrVersandTask?.last_error ?? null
    });
  }

  // OPPM Monatsübersicht QR-Code-Versand: analog.
  if (hasMonatsuebersichtQrVersand(view.customer) && view.deadlines.monatsuebersicht_qr_versand) {
    const muePeriode = view.monatsuebersichtQrPeriode ?? getLohnscheinPeriode(view.currentYear, view.currentMonth);
    const mueErledigt = view.monatsuebersichtQrCompleted || false;
    deadlines.push({
      key: 'monatsuebersicht_qr_versand',
      fieldName: 'monatsuebersicht_qr_versand_completed',
      title: `OPPM Monatsübersicht-Versand (für ${monatLabel(muePeriode.year, muePeriode.month)})`,
      date: view.deadlines.monatsuebersicht_qr_versand,
      completed: mueErledigt,
      type: 'standard',
      isSnoozed: isDeadlineSnoozed('monatsuebersicht_qr_versand_completed', view.status),
      snoozeInfo: getSnoozedInfo('monatsuebersicht_qr_versand_completed', view.status),
      auftragWartend: !!(view.monatsuebersichtQrVersandTask && !mueErledigt) && view.monatsuebersichtQrVersandTask?.status !== 'eskaliert',
      auftragEskaliert: view.monatsuebersichtQrVersandTask?.status === 'eskaliert',
      auftragFeedback: view.monatsuebersichtQrVersandTask?.last_error ?? null
    });
  }

  // NACHZÜGLER SPEZIALFRISTEN: offene QR-Fristen aus dem zuletzt abgeschlossenen
  // Monat. Ohne diese Kacheln verschwindet eine bei der Finalisierung noch
  // offene QR-Frist mit dem Monatswechsel aus der Oberfläche.
  (view.spezialfristNachzuegler || []).forEach(n => {
    const eskaliert = n.auftrag?.status === 'eskaliert';
    const titel = n.frist_art === 'oppm_qr_versand'
      ? 'OPPM QR-Code-Versand'
      : 'OPPM Monatsübersicht-Versand';
    deadlines.push({
      key: `nachzuegler_${n.frist_art}_${n.arbeitsjahr}_${n.arbeitsmonat}`,
      fieldName: n.frist_art === 'oppm_qr_versand'
        ? 'oppm_qr_versand_completed'
        : 'monatsuebersicht_qr_versand_completed',
      title: `${titel} (für ${monatLabel(n.periode.year, n.periode.month)})`,
      date: n.faelligkeit,
      completed: false,
      type: 'standard',
      spezialfristNachzuegler: n,
      auftragWartend: !!n.auftrag && !eskaliert,
      auftragEskaliert: eskaliert,
      auftragFeedback: n.auftrag?.last_error ?? null
    });
  });

  // ERWEITERT: Individuelle Deadlines mit Beschreibungen und One-Time Flag
  if (view.individualDeadlines && view.individualDeadlines.length > 0) {
    view.individualDeadlines.forEach(individualDeadline => {
      const deadlineKey = getIndividualDeadlineKey(individualDeadline.id);
      const deadlineDate = view.deadlines[deadlineKey];
      const fieldName = `individual_deadline_${individualDeadline.id}` as StatusField;
      
      // Für einmalige Deadlines: nutze completed Status aus der Deadline selbst
      const isCompleted = !individualDeadline.is_recurring 
        ? individualDeadline.completed || false
        : view.status?.individual_deadlines_status?.[individualDeadline.id] || false;
      
      if (deadlineDate) {
        deadlines.push({
          key: deadlineKey,
          fieldName,
          title: getIndividualDeadlineLabel(individualDeadline),
          date: deadlineDate,
          completed: isCompleted,
          type: 'individual',
          description: individualDeadline.description, // NEUE PROPERTY
          isOneTime: !individualDeadline.is_recurring, // NEUE PROPERTY
          isSnoozed: isDeadlineSnoozed(fieldName, view.status),
          snoozeInfo: getSnoozedInfo(fieldName, view.status)
        });
      }
    });
  }

  // NACHZÜGLER INDIVIDUELLE FRISTEN: offene individuelle Fristen aus dem zuletzt
  // abgeschlossenen Monat (nur Fristen mit offen_nach_finalisierung_ab). Ohne
  // diese Kacheln verschwindet eine bei der Finalisierung noch offene Frist mit
  // dem Monatswechsel aus der Oberfläche. Eigener Schlüssel und Feldname, damit
  // die reguläre Kachel derselben Frist im laufenden Monat unberührt bleibt.
  (view.individualNachzuegler || []).forEach(n => {
    const nachzueglerKey = getIndividualNachzueglerKey(n);
    deadlines.push({
      key: nachzueglerKey,
      fieldName: nachzueglerKey as StatusField,
      title: n.titel,
      date: n.faelligkeit,
      completed: false,
      type: 'individual',
      description: n.beschreibung,
      individualNachzuegler: n
    });
  });

  // VERBESSERTE SORTIERUNG: Erst nach Status (unerledigt zuerst), dann nach Datum
  return deadlines.sort((a, b) => {
    // Schritt 1: Erledigte nach hinten
    if (a.completed !== b.completed) {
      return a.completed ? 1 : -1; // Unerledigte zuerst
    }
    
    // Schritt 2: Bei gleichem Status nach Datum sortieren
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return a.date.getTime() - b.date.getTime();
  });
};

  const getButtonText = (deadline: DeadlineItem, isLoading: boolean, anlieferungActive: boolean = false): string => {
    if (isLoading) {
      return 'Prüfung läuft...';
    }
    
    // Markiere einmalige Deadlines visuell
    // FRIST AB ANLIEFERUNG: Sanduhr-Präfix signalisiert "die Frist läuft aktiv"
    const prefix = anlieferungActive ? '⏳ ' : (deadline.isOneTime ? '📌 ' : '');
    return prefix + deadline.title;
  };

  const auftraggeberName = getAuftraggeberName();
  const ticketStand = formatTicketStand(view.openTicketsStand);

  return (
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @keyframes anlieferungPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.45); }
            50% { box-shadow: 0 0 0 5px rgba(249, 115, 22, 0); }
          }
          
          .anlieferung-active {
            animation: anlieferungPulse 2s ease-in-out infinite;
          }
          
          .loading-spinner {
            display: inline-block;
            width: 12px;
            height: 12px;
            border: 2px solid transparent;
            border-top: 2px solid currentColor;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-right: 4px;
          }
          
          .deadline-item {
            position: relative;
          }
          
          .deadline-tooltip {
            position: absolute;
            bottom: calc(100% + 8px);
            left: 50%;
            transform: translateX(-50%);
            background: rgba(31, 41, 55, 0.95);
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            white-space: normal;
            max-width: 250px;
            z-index: 100;
            pointer-events: none;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            line-height: 1.4;
          }
          
          .deadline-tooltip::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 50%;
            margin-left: -5px;
            border-width: 5px;
            border-style: solid;
            border-color: rgba(31, 41, 55, 0.95) transparent transparent transparent;
          }
          
          .snooze-tooltip {
            position: absolute;
            bottom: calc(100% + 4px);
            right: 0;
            background: rgba(31, 41, 55, 0.95);
            color: white;
            padding: 6px 10px;
            border-radius: 6px;
            font-size: 11px;
            white-space: nowrap;
            z-index: 100;
            pointer-events: none;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          }
          
          .snooze-tooltip::after {
            content: '';
            position: absolute;
            top: 100%;
            right: 8px;
            border-width: 4px;
            border-style: solid;
            border-color: rgba(31, 41, 55, 0.95) transparent transparent transparent;
          }
          
          .snooze-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            border: none;
            cursor: pointer;
            transition: all 0.2s;
            padding: 0;
            background-color: #F3F4F6;
            color: #6B7280;
            flex-shrink: 0;
          }
          
          .snooze-btn:hover {
            background-color: #FEF3C7;
            color: #92400E;
          }
          
          .markierung-x {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: none;
            cursor: pointer;
            padding: 0;
            background-color: transparent;
            color: inherit;
            opacity: 0.6;
            flex-shrink: 0;
          }
          
          .markierung-x:hover {
            color: #991B1B;
            opacity: 1;
          }
        `}
      </style>
      
      <div style={{
        padding: '16px',
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        marginBottom: '16px',
        ...(isCurrentlyOverdue ? { borderColor: '#EF4444', backgroundColor: '#FEF2F2' } : {})
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '16px',
                color: '#111827',
                display: 'flex', 
                alignItems: 'center'
              }}>
                <span style={{ fontWeight: 'bold' }}>
                  {view.customer.company}
                </span>
                {auftraggeberName && (
                  <span style={{ fontWeight: 'normal', color: '#6B7280' }}>
                    {'\u00A0(im Auftrag von '}{auftraggeberName}{')'}
                  </span>
                )}
              </div>
              <div style={{
                fontSize: '14px', 
                color: '#6B7280',
                display: 'flex',
                alignItems: 'center'
              }}>
                {getDisplayName(view.customer)}
                {view.customer.zendesk_tag && (
                  <a 
                    href={buildZendeskAgentSuchUrl(view.customer.zendesk_tag)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                    title={ticketStand || undefined}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <span style={getTicketsBadgeStyle(view.openTicketsCount)}>
                      {view.openTicketsCount !== undefined 
                        ? `${view.openTicketsCount} Tickets`
                        : 'Lade...'
                      }
                    </span>
                  </a>
                )}
                {view.customer.postalischer_versand && (
                  <div 
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      backgroundColor: '#EEF2FF',
                      color: '#4338CA',
                      fontSize: '11px',
                      fontWeight: '600',
                      padding: '3px 6px',
                      borderRadius: '4px',
                      whiteSpace: 'nowrap',
                      marginLeft: '8px'
                    }}
                    title="Postalischer Versand erforderlich"
                  >
                    <Truck size={12} />
                    <span>POST</span>
                  </div>
                )}
                {view.customer.zustaendiger_mitarbeiter && (
                  <span 
                    style={{
                      padding: '3px 6px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      marginLeft: '8px',
                      backgroundColor: '#F0F9FF',
                      color: '#0369A1',
                      display: 'inline-block'
                    }}
                    title={`Sachbearbeiter: ${view.customer.zustaendiger_mitarbeiter.full_name}`}
                  >
                    {view.customer.zustaendiger_mitarbeiter.initials}
                  </span>
                )}
              </div>
            </div>
            
            <div style={{ position: 'relative', display: 'flex', gap: '8px' }}>
              {/* Edit Button */}
              {onEditCustomer && (
                <div
                  onClick={() => onEditCustomer(view.customer)}
                  style={{
                    cursor: 'pointer',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#E0E7FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#C7D2FE'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#E0E7FF'}
                  title="Kunde bearbeiten"
                >
                  <Edit2 size={12} color="#4F46E5" />
                </div>
              )}
              
              {/* Reset Button */}
              {onResetMonth && (
                <div style={{ position: 'relative' }}>
                  <div
                    onClick={() => setShowResetConfirm(true)}
                    style={{
                      cursor: 'pointer',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#FEE2E2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: '#DC2626',
                      fontWeight: 'bold',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FCA5A5'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FEE2E2'}
                    title="Monat zurücksetzen"
                  >
                    ↺
                  </div>
                  
                  {showResetConfirm && (
                    <div style={{
                      position: 'absolute',
                      top: '24px',
                      left: '0',
                      backgroundColor: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '16px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      zIndex: 20,
                      minWidth: '280px'
                    }}>
                      <p style={{ 
                        fontSize: '14px', 
                        marginBottom: '12px',
                        color: '#374151'
                      }}>
                        <strong>Monat zurücksetzen?</strong>
                      </p>
                      <p style={{ 
                        fontSize: '12px', 
                        marginBottom: '16px',
                        color: '#6B7280'
                      }}>
                        Der Kunde wird auf {view.currentMonth - 1 || 12}/{view.currentMonth === 1 ? view.currentYear - 1 : view.currentYear} zurückgesetzt.
                        Alle Erledigungen für den aktuellen Monat werden entfernt.
                      </p>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setShowResetConfirm(false)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#F3F4F6',
                            color: '#374151',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          Abbrechen
                        </button>
                        <button
                          onClick={() => {
                            onResetMonth(view);
                            setShowResetConfirm(false);
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#DC2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          Zurücksetzen
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Info Icon */}
              <div
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                style={{
                  cursor: 'pointer',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: '#E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  color: '#6B7280',
                  fontWeight: 'bold'
                }}
              >
                i
              </div>
              
              {showTooltip && (
                <div style={{
                  position: 'absolute',
                  top: '24px',
                  right: '0',
                  backgroundColor: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '12px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 10,
                  minWidth: '250px',
                  fontSize: '12px'
                }}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ color: '#374151' }}>Beraternummer:</strong>
                    <span style={{ color: '#6B7280', marginLeft: '8px' }}>
                      {view.customer.beraternummer || 'Nicht hinterlegt'}
                    </span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ color: '#374151' }}>Mandantennummer:</strong>
                    <span style={{ color: '#6B7280', marginLeft: '8px' }}>
                      {view.customer.mandantennummer || 'Nicht hinterlegt'}
                    </span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ color: '#374151' }}>Abrechnungssoftware:</strong>
                    <span style={{ color: '#6B7280', marginLeft: '8px' }}>
                      {getAbrechnungsmethodeText(view.customer.abrechnungsmethode)}
                    </span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ color: '#374151' }}>Versandmethode:</strong>
                    <span style={{ color: '#6B7280', marginLeft: '8px' }}>
                      {getVersandmethodeText(view.customer.versandmethode)}
                    </span>
                  </div>
                  <div>
                    <strong style={{ color: '#374151' }}>Postalischer Versand:</strong>
                    <span style={{ color: '#6B7280', marginLeft: '8px' }}>
                      {view.customer.postalischer_versand ? 'Aktiviert' : 'Nicht aktiviert'}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Send Documents Button */}
              {onSendDocuments && (
                <div
                  onClick={() => onSendDocuments(view.customer)}
                  style={{
                    cursor: 'pointer',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#D1FAE5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#A7F3D0'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#D1FAE5'}
                  title="Dokumente senden"
                >
                  <FileUp size={12} color="#059669" />
                </div>
              )}
            </div>
          </div>
          <div style={{fontSize: '12px', color: '#6B7280'}}>
            {view.currentMonth}/{view.currentYear}
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '12px'
        }}>
          {getSortedDeadlines().map((deadline) => {
            const buttonLoading = isButtonLoading(view.customer.id, deadline.fieldName);
            const isHovered = hoveredDeadlineId === deadline.key;
            const isSnoozeHovered = hoveredSnoozeId === deadline.key;
            // WARTET AUF KUNDEN: Nachzügler gehören zu abgeschlossenen Monaten
            // und ruhen nie, auch wenn sie denselben Feldnamen tragen.
            const ruhend = !deadline.completed && !deadline.nachzuegler && !deadline.spezialfristNachzuegler && !deadline.individualNachzuegler && ruhendeFristen.has(deadline.fieldName);
            const wartetAufKunden = ruhend && (deadline.fieldName === 'lohnlauf_1_completed' || deadline.fieldName === 'lohnlauf_2_completed');
            const anlieferungActive = !buttonLoading && !ruhend && isAnlieferungActive(deadline);
            // WARTET AUF KUNDEN: Ablauf per Klick auf die Kachel.
            // Daten erhalten -> Klick = an Kunden gesendet (versandOffen)
            // wartet auf Kunden -> Klick = Rückmeldung erhalten (wartetAufKunden)
            // nach Rückmeldung -> Klick = erledigen bzw. finalisieren (nachRueckmeldung)
            const anlieferung = deadline.fristAbAnlieferung && !deadline.completed ? deadline.anlieferungInfo : null;
            const versandOffen = !!anlieferung && !anlieferung.beim_kunden_seit;
            const nachRueckmeldung = !!anlieferung && !!anlieferung.beim_kunden_seit && !wartetAufKunden;
            const waitingForDaten = isWaitingForDaten(deadline);
            const istNachzuegler = !!deadline.nachzuegler || !!deadline.spezialfristNachzuegler || !!deadline.individualNachzuegler;
            
            return (
              <div 
                key={deadline.key}
                className={`deadline-item${anlieferungActive ? ' anlieferung-active' : ''}`}
                style={{
                  padding: '8px',
                  border: istNachzuegler
                    ? '2px dashed #B45309'
                    : (deadline.isOneTime ? '2px dashed #9CA3AF' : '1px solid #d1d5db'),
                  borderRadius: '4px',
                  cursor: buttonLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  position: 'relative',
                  ...getOverdueStyle(deadline.date, deadline.completed, buttonLoading, deadline.isSnoozed, anlieferungActive, deadline.auftragWartend || false, deadline.auftragEskaliert || false, ruhend),
                  ...(anlieferungActive ? { border: '2px solid #F97316' } : {})
                }}
                onClick={() => {
                  if (buttonLoading) return;
                  // NACHZÜGLER: eigener Klickpfad. Die Kachel gehört zu einem
                  // abgeschlossenen Monat, deshalb wird hier NICHT der Status des
                  // laufenden Arbeitsmonats getoggelt.
                  if (deadline.nachzuegler) {
                    if (onOpenLohnscheinNachzuegler) {
                      onOpenLohnscheinNachzuegler(view, deadline.nachzuegler);
                    }
                    return;
                  }
                  // NACHZÜGLER SPEZIALFRISTEN: gleiche Begründung, eigener Pfad.
                  if (deadline.spezialfristNachzuegler) {
                    if (onOpenSpezialfristNachzuegler) {
                      onOpenSpezialfristNachzuegler(view, deadline.spezialfristNachzuegler);
                    }
                    return;
                  }
                  // NACHZÜGLER INDIVIDUELLE FRISTEN: gleiche Begründung, eigener
                  // Pfad. Der Klick erledigt die Frist für den abgeschlossenen Monat.
                  if (deadline.individualNachzuegler) {
                    if (onCompleteIndividualNachzuegler) {
                      onCompleteIndividualNachzuegler(view, deadline.individualNachzuegler);
                    }
                    return;
                  }
                  // FRIST AB ANLIEFERUNG: Im Wartezustand markiert der reguläre
                  // Klick die Datenanlieferung; danach (und bei Kunden ohne
                  // Toggle) togglet er wie gewohnt die Erledigung.
                  if (waitingForDaten && onMarkDatenAnlieferung) {
                    onMarkDatenAnlieferung(view, deadline.fieldName);
                    return;
                  }
                  // WARTET AUF KUNDEN: Liegt die Abrechnung beim Kunden, markiert
                  // der reguläre Klick die Kundenrückmeldung. Die Frist läuft
                  // dann neu, der nächste Klick erledigt wie gewohnt.
                  if (wartetAufKunden && onMarkKundenRueckmeldung) {
                    onMarkKundenRueckmeldung(view, deadline.fieldName);
                    return;
                  }
                  // WARTET AUF KUNDEN: Nach dem Datenerhalt markiert der reguläre
                  // Klick den Versand an den Kunden. Wer ohne Versand abschließen
                  // will, nutzt das Häkchen-Symbol auf der Kachel.
                  if (versandOffen && onMarkWartetAufKunden) {
                    onMarkWartetAufKunden(view, deadline.fieldName);
                    return;
                  }
                  onToggleStatus(view, deadline.fieldName);
                }}
                onMouseEnter={() => deadline.description && setHoveredDeadlineId(deadline.key)}
                onMouseLeave={() => setHoveredDeadlineId(null)}
              >
                {/* Beschreibungs-Tooltip */}
                {deadline.description && isHovered && (
                  <div className="deadline-tooltip">
                    {deadline.description}
                  </div>
                )}
                
                <div style={{fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px'}}>
                  <span style={{ flex: 1 }}>
                    {buttonLoading ? (
                      <span className="loading-spinner"></span>
                    ) : (
                      getStatusIcon(deadline.completed, buttonLoading, deadline.isOneTime || false)
                    )}{' '}
                    {getButtonText(deadline, buttonLoading, anlieferungActive)}
                  </span>
                  
                  {/* SNOOZE FEATURE: Snooze-Badge wenn zurückgestellt */}
                  {deadline.isSnoozed && deadline.snoozeInfo && (
                    <span
                      style={{
                        position: 'relative',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                        backgroundColor: '#FEF3C7',
                        color: '#92400E',
                        fontSize: '10px',
                        fontWeight: '600',
                        padding: '2px 5px',
                        borderRadius: '3px',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => {
                        e.stopPropagation();
                        setHoveredSnoozeId(deadline.key);
                      }}
                      onMouseLeave={() => setHoveredSnoozeId(null)}
                    >
                      <Clock size={10} />
                      {deadline.snoozeInfo.snoozed_by_initials}
                      
                      {/* Snooze-Tooltip auf Hover */}
                      {isSnoozeHovered && (
                        <span className="snooze-tooltip">
                          Zurückgestellt von {deadline.snoozeInfo.snoozed_by}
                        </span>
                      )}
                    </span>
                  )}
                  
                  {/* SNOOZE FEATURE: Snooze-Button für unerledigte, nicht-gesnoozde Deadlines.
                      NACHZÜGLER sind bewusst ausgenommen: Snooze schreibt in den Monatsstatus
                      des Arbeitsmonats, was für einen abgeschlossenen Monat der falsche
                      Datensatz wäre. Wer die Kachel loswerden will, nutzt im Modal
                      "Ohne Versand als erledigt markieren"; bei individuellen
                      Fristen erledigt der Klick auf die Kachel selbst. */}
                  {!deadline.completed && !deadline.isSnoozed && !buttonLoading && !istNachzuegler && !ruhend && onSnoozeDeadline && deadline.date && deadline.date < new Date() && (
                    <button
                      className="snooze-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSnoozeDeadline(view, deadline.fieldName);
                      }}
                      title="Deadline auf morgen zurückstellen"
                    >
                      <Clock size={11} />
                    </button>
                  )}
                  
                  {/* WARTET AUF KUNDEN: Abschluss ohne Versand an den Kunden. Nur im
                      Zustand "Daten erhalten", in dem der Kachel-Klick den Versand
                      an den Kunden markiert. */}
                  {versandOffen && !buttonLoading && onMarkWartetAufKunden && (
                    <button
                      className="snooze-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleStatus(view, deadline.fieldName);
                      }}
                      title="Ohne Versand an den Kunden abschließen"
                    >
                      <Check size={11} />
                    </button>
                  )}
                  
                  {/* WARTET AUF KUNDEN: weitere Korrekturrunde nach einer
                      Rückmeldung. Die Frist ruht dann erneut. */}
                  {nachRueckmeldung && !buttonLoading && onMarkWartetAufKunden && (
                    <button
                      className="snooze-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onMarkWartetAufKunden(view, deadline.fieldName);
                      }}
                      title="Erneut an Kunden gesendet: Frist ruht bis zur Rückmeldung"
                    >
                      <Send size={11} />
                    </button>
                  )}
                </div>
                {/* WARTET AUF KUNDEN: Beim Lohnlauf selbst zeigen die Textzeilen
                    unten den Stand, die Datumszeile entfällt. Daran hängende
                    Fristen behalten den Hinweis, weil ihr Datum bis zur
                    Rückmeldung keine Bedeutung hat. */}
                {!wartetAufKunden && (
                  <div style={{fontSize: '11px', ...(istNachzuegler ? { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' } : {})}}>
                    {ruhend ? 'Frist ruht bis zur Kundenrückmeldung' : (deadline.date ? formatDate(deadline.date) : 'Nicht berechnet')}
                    {/* NACHZÜGLER: Herkunftsmonat sichtbar machen, damit klar ist,
                        dass diese Kachel nicht zum angezeigten Arbeitsmonat gehört.
                        Steht in der Datumszeile, weil die immer kurz ist und das
                        Badge dort auch bei langen Titeln innerhalb der Kachel bleibt. */}
                    {(deadline.nachzuegler || deadline.spezialfristNachzuegler || deadline.individualNachzuegler) && (() => {
                      const herkunft = deadline.nachzuegler ?? deadline.spezialfristNachzuegler ?? deadline.individualNachzuegler!;
                      return (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: '#FEF3C7',
                            color: '#92400E',
                            fontSize: '10px',
                            fontWeight: '600',
                            padding: '2px 5px',
                            borderRadius: '3px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                          }}
                          title={`Noch offen aus dem abgeschlossenen Monat ${monatLabel(herkunft.arbeitsjahr, herkunft.arbeitsmonat)}`}
                        >
                          Nachzügler {monatLabel(herkunft.arbeitsjahr, herkunft.arbeitsmonat)}
                        </span>
                      );
                    })()}
                  </div>
                )}
                {deadline.auftragEskaliert && (
                  <div style={{fontSize: '10px', color: '#991B1B', marginTop: '2px', fontStyle: 'italic'}}>
                    {deadline.auftragFeedback || 'Auftrag angehalten — es wurde nichts versendet.'} Ein Klick legt einen neuen Auftrag an.
                  </div>
                )}
                {deadline.auftragWartend && !deadline.auftragEskaliert && (
                  <div style={{fontSize: '10px', color: '#92400E', marginTop: '2px', fontStyle: 'italic'}}>
                    {deadline.auftragFeedback || 'Beauftragt — Versand läuft automatisch, sobald Dokumente vorliegen.'}
                  </div>
                )}
                {/* FRIST AB ANLIEFERUNG / WARTET AUF KUNDEN: Textzeilen am unteren
                    Rand der Kachel mit Datum, Uhrzeit und Kürzel. Das X nimmt den
                    jeweils letzten Schritt zurück (Fehlklick-Korrektur); das X bei
                    "Daten erhalten" setzt die Markierung komplett zurück. */}
                {anlieferung && (
                  <div style={{marginTop: '4px', fontSize: '10px', lineHeight: '1.5'}}>
                    <div
                      style={{display: 'flex', alignItems: 'center', gap: '4px'}}
                      title={`Markiert von ${anlieferung.marked_by}`}
                    >
                      <span>
                        Daten erhalten: {formatZeitpunkt(anlieferung.angeliefert_am, anlieferung.marked_at)} · {anlieferung.marked_by_initials}
                      </span>
                      {onUnmarkDatenAnlieferung && (
                        <button
                          className="markierung-x"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUnmarkDatenAnlieferung(view, deadline.fieldName);
                          }}
                          title="Markierung Daten erhalten zurücknehmen"
                        >
                          <X size={10} />
                        </button>
                      )}
                    </div>
                    
                    {anlieferung.beim_kunden_seit && (
                      <div
                        style={{display: 'flex', alignItems: 'center', gap: '4px'}}
                        title={`Markiert von ${anlieferung.beim_kunden_by || ''}`}
                      >
                        <span>
                          An Kunden: {formatZeitpunkt(anlieferung.beim_kunden_seit, anlieferung.beim_kunden_at)} · {anlieferung.beim_kunden_by_initials}
                        </span>
                        {wartetAufKunden && onUnmarkWartetAufKunden && (
                          <button
                            className="markierung-x"
                            onClick={(e) => {
                              e.stopPropagation();
                              onUnmarkWartetAufKunden(view, deadline.fieldName);
                            }}
                            title="Markierung An Kunden zurücknehmen"
                          >
                            <X size={10} />
                          </button>
                        )}
                      </div>
                    )}
                    
                    {nachRueckmeldung && anlieferung.rueckmeldung_am && (
                      <div
                        style={{display: 'flex', alignItems: 'center', gap: '4px'}}
                        title={`Markiert von ${anlieferung.rueckmeldung_by || ''}`}
                      >
                        <span>
                          Rückmeldung: {formatZeitpunkt(anlieferung.rueckmeldung_am, anlieferung.rueckmeldung_at)} · {anlieferung.rueckmeldung_by_initials}
                        </span>
                        {onUnmarkKundenRueckmeldung && (
                          <button
                            className="markierung-x"
                            onClick={(e) => {
                              e.stopPropagation();
                              onUnmarkKundenRueckmeldung(view, deadline.fieldName);
                            }}
                            title="Markierung Rückmeldung zurücknehmen"
                          >
                            <X size={10} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};
